import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Stack,
  Grid,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  useTheme
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TodayIcon from '@mui/icons-material/Today';
import BlockIcon from '@mui/icons-material/Block';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import { startOfWeek, endOfWeek, addDays, format, isToday, isSameDay, addWeeks, subWeeks, parseISO } from 'date-fns';
import { createUnavailableBlock, getUnavailableBlocks } from '../../api/calendarService';
import { enAU } from 'date-fns/locale';

// Time constants
const START_HOUR = 8; // 8:00 AM
const END_HOUR = 21;  // 9:00 PM
const HOUR_HEIGHT = 60; // pixels per hour
const TIME_SLOT_HEIGHT = HOUR_HEIGHT / 4; // 15-minute slots

interface WeekViewCalendarProps {
  onClose: () => void;
  practitionerId?: string;
  onBlocksCreated: () => void;
  initialDate?: Date;
}

interface TimeSlot {
  day: Date;
  hour: number;
  minute: number;
  isSelectable: boolean;
}

interface SelectedTimeRange {
  startDay: Date | null;
  startHour: number | null;
  startMinute: number | null;
  endDay: Date | null;
  endHour: number | null;
  endMinute: number | null;
}

interface UnavailableBlock {
  id: string;
  starts_at: string;
  ends_at: string;
  notes?: string;
  unavailable_block_type_id?: string;
  unavailable_block_type_name?: string; // New field from API
  unavailable_block_type?: {
    name: string;
    color: string;
  };
}

const WeekViewCalendar: React.FC<WeekViewCalendarProps> = ({
  onClose,
  practitionerId,
  onBlocksCreated,
  initialDate
}) => {
  const theme = useTheme();
  // Calendar state
  const [currentDate, setCurrentDate] = useState<Date>(initialDate || new Date());
  const [weekStart, setWeekStart] = useState<Date>(startOfWeek(currentDate, { weekStartsOn: 0 }));
  const [weekDays, setWeekDays] = useState<Date[]>([]);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[][]>([]);
  
  // Two-click selection state
  const [selectionPhase, setSelectionPhase] = useState<'none' | 'first' | 'complete'>('none');
  const [firstSelection, setFirstSelection] = useState<{ day: number, slot: number } | null>(null);
  const [secondSelection, setSecondSelection] = useState<{ day: number, slot: number } | null>(null);
  const [selectedRange, setSelectedRange] = useState<SelectedTimeRange>({
    startDay: null,
    startHour: null,
    startMinute: null,
    endDay: null,
    endHour: null,
    endMinute: null
  });
  
  // Track created blocks for visual feedback
  const [createdBlocks, setCreatedBlocks] = useState<Array<{
    dayIndex: number;
    startSlot: number;
    endSlot: number;
  }>>([]);

  // Fetched unavailable blocks
  const [unavailableBlocks, setUnavailableBlocks] = useState<UnavailableBlock[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [blockNotes, setBlockNotes] = useState<string>('');
  const [blockTypeId, setBlockTypeId] = useState<string>('');
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  
  // Refs
  const timeGridRef = useRef<HTMLDivElement>(null);

  // Initialize the week days
  // Use the practitioner ID that was provided
  console.log("Practitioner ID provided to WeekViewCalendar:", practitionerId);
  
  // Try to fetch blocks using the provided practitioner ID
  const tryAllPractitionerIds = async () => {
    setIsLoading(true);
    setMessage('Using provided practitioner ID...');
    setShowMessage(true);
    
    const start = startOfWeek(currentDate, { weekStartsOn: 0 });
    const end = endOfWeek(currentDate, { weekStartsOn: 0 });
    const startStr = start.toISOString().split('.')[0] + 'Z';
    const endStr = end.toISOString().split('.')[0] + 'Z';
    
    // Get token from localStorage for authorization
    const token = localStorage.getItem('token');
    const headers: HeadersInit = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    
    // Only use the specific practitioner ID provided
    try {
      if (!practitionerId) {
        console.error("No practitioner ID provided");
        setMessage("No practitioner ID provided");
        setShowMessage(true);
        setIsLoading(false);
        return null;
      }
      
      console.log(`Fetching unavailable blocks for practitioner ID: ${practitionerId}`);
      
      // Make sure we're only using the selected practitioner's ID
      const apiUrl = `/api/unavailable-blocks?starts_at_gteq=${encodeURIComponent(startStr)}&ends_at_lteq=${encodeURIComponent(endStr)}&practitioner_id=${practitionerId}`;
      
      const response = await fetch(apiUrl, { method: 'GET', headers });
      
      if (response.ok) {
        const data = await response.json();
        const blocks = data.unavailable_blocks || [];
        
        console.log(`Found ${blocks.length} blocks for practitioner ID: ${practitionerId}`);
        console.log(`Blocks:`, blocks);
        
        setUnavailableBlocks(blocks);
        return practitionerId;
      } else {
        console.error(`Error fetching blocks: ${response.status}`);
        setMessage(`Error fetching blocks: ${response.status}`);
        setShowMessage(true);
      }
    } catch (err) {
      console.error(`Error fetching unavailable blocks:`, err);
      setMessage('Error fetching unavailable blocks');
      setShowMessage(true);
    }
    
    setMessage('No blocks found with provided practitioner ID');
    setShowMessage(true);
    setIsLoading(false);
    return null;
  };

  // Direct API call using fetch to bypass any potential service layer issues
  const fetchUnavailableBlocksDirect = async () => {
    try {
      if (!practitionerId) {
        console.error("No practitioner ID provided for fetchUnavailableBlocksDirect");
        setMessage("Error: No practitioner ID provided");
        setShowMessage(true);
        return [];
      }
      
      setIsLoading(true);
      const start = startOfWeek(currentDate, { weekStartsOn: 0 });
      const end = endOfWeek(currentDate, { weekStartsOn: 0 });
      
      // Convert to ISO string but truncate milliseconds for cleaner URLs
      const startStr = start.toISOString().split('.')[0] + 'Z';
      const endStr = end.toISOString().split('.')[0] + 'Z';
      
      // Construct API URL with the practitioner ID
      const apiUrl = `/api/unavailable-blocks?starts_at_gteq=${encodeURIComponent(startStr)}&ends_at_lteq=${encodeURIComponent(endStr)}&practitioner_id=${encodeURIComponent(practitionerId)}`;
      
      console.log('Making direct fetch API call to:', apiUrl);
      console.log('Using provided practitioner ID:', practitionerId);
      setMessage(`Fetching unavailable blocks for ${practitionerId}...`);
      setShowMessage(true);
      
      // Get token from localStorage for authorization
      const token = localStorage.getItem('token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      };
      
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Make direct fetch call
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Direct fetch response:', data);
      
      // Use the response data directly
      const blocks = data.unavailable_blocks || [];
      console.log('Parsed blocks from direct fetch:', blocks);
      
      setMessage(`Found ${blocks.length} unavailable blocks`);
      setShowMessage(true);
      setUnavailableBlocks(blocks);
      
      return blocks;
    } catch (err) {
      console.error('Error in direct fetch:', err);
      setError('Failed to load unavailable blocks: ' + (err as Error).message);
      setShowMessage(true);
      return [];
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch unavailable blocks for the current week
  const fetchUnavailableBlocks = async () => {
    // Log all props for debugging
    console.log("WeekViewCalendar props:", { onClose, practitionerId, onBlocksCreated, initialDate });
    console.log("Using practitioner ID:", practitionerId);
    
    try {
      // Try direct fetch first for debugging
      const directResult = await fetchUnavailableBlocksDirect();
      if (directResult.length > 0) {
        // If direct fetch worked, we're done
        return;
      }
      
      // Otherwise try the regular service approach as backup
      setIsLoading(true);
      const start = startOfWeek(currentDate, { weekStartsOn: 0 });
      const end = endOfWeek(currentDate, { weekStartsOn: 0 });
      
      // Convert to ISO string but truncate milliseconds for cleaner URLs
      const startStr = start.toISOString().split('.')[0] + 'Z';
      const endStr = end.toISOString().split('.')[0] + 'Z';
      
      // Log the exact URL we're going to request for debugging
      const apiUrl = `/api/unavailable-blocks?starts_at_gteq=${encodeURIComponent(startStr)}&ends_at_lteq=${encodeURIComponent(endStr)}&practitioner_id=${encodeURIComponent(practitionerId)}`;
      console.log('API request URL (without base):', apiUrl);
      
      console.log('Fetching unavailable blocks for week:', {
        startDate: startStr,
        endDate: endStr,
        practitionerId: practitionerId
      });
      
      setMessage(`Fetching unavailable blocks for ${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`);
      setShowMessage(true);
      
      const response = await getUnavailableBlocks({
        startDate: startStr,
        endDate: endStr,
        practitionerId: practitionerId
      });
      
      console.log('Received unavailable blocks:', response);
      
      // Check for both possible API response formats
      const blocks = Array.isArray(response) 
        ? response 
        : (response.unavailable_blocks || []);
      
      console.log('Parsed blocks:', blocks);
      
      if (blocks.length === 0) {
        setMessage(`No unavailable blocks found for this week`);
      } else {
        setMessage(`Found ${blocks.length} unavailable block(s)`);
      }
      setShowMessage(true);
      
      setUnavailableBlocks(blocks);
      
      // Log the processed blocks for debugging
      setTimeout(() => {
        const processedBlocks = processUnavailableBlocks();
        console.log(`Processed ${processedBlocks.length} unavailable blocks for practitioner ${practitionerId}:`, processedBlocks);
        if (processedBlocks.length === 0) {
          console.log('No blocks found to display. This could be normal if:');
          console.log('1. This practitioner has no unavailable blocks set');
          console.log('2. The blocks are outside the current week view');
        }
      }, 100);
    } catch (err) {
      console.error('Error fetching unavailable blocks:', err);
      setError('Failed to load existing unavailable blocks');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to get color for unavailable block type
  const getUnavailableBlockColor = (typeName?: string): string => {
    if (!typeName) return 'rgba(244, 67, 54, 0.7)'; // Default red
    
    // Map common unavailable block types to colors
    switch(typeName.toLowerCase()) {
      case 'lunch':
      case 'lunch break':
        return 'rgba(255, 152, 0, 0.7)'; // Orange
      case 'meeting':
      case 'meeting / appointment':
        return 'rgba(63, 81, 181, 0.7)'; // Indigo
      case 'out of office':
      case 'away':
        return 'rgba(156, 39, 176, 0.7)'; // Purple
      default:
        return 'rgba(244, 67, 54, 0.7)'; // Default red
    }
  };

  // Initialize the week days and fetch unavailable blocks
  
  useEffect(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 0 });
    setWeekStart(start);
    
    // Generate array of days for the week
    const days = [];
    for (let i = 0; i < 7; i++) {
      days.push(addDays(start, i));
    }
    setWeekDays(days);
    
    // Create time slots for each day
    const slots: TimeSlot[][] = [];
    
    // For each day
    for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
      const day = days[dayIndex];
      const daySlots: TimeSlot[] = [];
      
      // For each hour
      for (let hour = START_HOUR; hour < END_HOUR; hour++) {
        // 15-minute increments
        for (let minute = 0; minute < 60; minute += 15) {
          daySlots.push({
            day,
            hour,
            minute,
            isSelectable: true
          });
        }
      }
      
      slots.push(daySlots);
    }
    
    setTimeSlots(slots);
    
    // Clear created blocks when changing weeks
    setCreatedBlocks([]);
    
    // Fetch unavailable blocks for this week using the selected practitioner ID
    console.log("useEffect triggered with currentDate:", currentDate, "using provided practitionerId:", practitionerId);
    // Only fetch if we have a practitioner ID
    if (practitionerId) {
      fetchUnavailableBlocksDirect();
    } else {
      console.error("No practitioner ID available for fetching unavailable blocks");
    }
    
    // Cleanup function to reset state when component unmounts
    return () => {
      setUnavailableBlocks([]);
      setCreatedBlocks([]);
    };
  }, [currentDate, practitionerId]);

  // Navigate to previous week
  const handlePrevWeek = () => {
    setCurrentDate(subWeeks(currentDate, 1));
  };

  // Navigate to next week
  const handleNextWeek = () => {
    setCurrentDate(addWeeks(currentDate, 1));
  };

  // Navigate to current week
  const handleCurrentWeek = () => {
    setCurrentDate(new Date());
  };

  // Convert slot coordinates to time
  const slotToTime = (dayIndex: number, slotIndex: number): { day: Date, hour: number, minute: number } => {
    const day = weekDays[dayIndex];
    const hour = Math.floor(slotIndex / 4) + START_HOUR;
    const minute = (slotIndex % 4) * 15;
    
    return { day, hour, minute };
  };
  
  // Convert time to slot index
  const timeToSlotIndex = (date: Date): number => {
    const hour = date.getHours();
    const minute = date.getMinutes();
    
    // Calculate slots since start hour
    return (hour - START_HOUR) * 4 + Math.floor(minute / 15);
  };
  
  // Get block color based on name or default color
  const getBlockColor = (block: UnavailableBlock): string => {
    console.log("Getting color for block:", block);
    
    // First try the color from the type object if it exists
    if (block.unavailable_block_type?.color) {
      return block.unavailable_block_type.color;
    }
    
    // If we have the type name directly, use that for color
    if (block.unavailable_block_type_name) {
      return getUnavailableBlockColor(block.unavailable_block_type_name);
    }
    
    // Finally, try to determine color from block ID (for emergencies)
    // Different ID patterns can help identify the block type 
    if (block.id) {
      if (block.id.includes('lunch')) return 'rgba(255, 152, 0, 0.7)';
      if (block.id.includes('meeting')) return 'rgba(63, 81, 181, 0.7)';
      if (block.id.includes('office') || block.id.includes('away')) return 'rgba(156, 39, 176, 0.7)';
    }
    
    // Default fallback color
    return 'rgba(244, 67, 54, 0.7)';
  };
  
  // Process unavailable blocks into visual blocks
  const processUnavailableBlocks = (): Array<{
    id: string;
    dayIndex: number;
    startSlot: number;
    endSlot: number;
    color: string;
    title: string;
  }> => {
    if (!weekDays.length || !unavailableBlocks.length) return [];
    
    return unavailableBlocks
      .filter(block => {
        // Filter blocks that occur within this week's view
        const startDate = parseISO(block.starts_at);
        const endDate = parseISO(block.ends_at);
        
        // Check if block starts or ends within this week, or spans the week
        return weekDays.some(day => 
          isSameDay(startDate, day) || 
          isSameDay(endDate, day) ||
          (startDate < day && endDate > day)
        );
      })
      .flatMap(block => {
        const startDate = parseISO(block.starts_at);
        const endDate = parseISO(block.ends_at);
        const result = [];
        
        // Check if the block spans multiple days
        if (!isSameDay(startDate, endDate)) {
          // For each day in the week, check if the block falls on that day
          weekDays.forEach((day, dayIndex) => {
            if (isSameDay(startDate, day)) {
              // This is the first day of the block
              const startSlot = timeToSlotIndex(startDate);
              // End at the end of the day
              const endSlot = (END_HOUR - START_HOUR) * 4 - 1;
              
              result.push({
                id: `${block.id}-day-${dayIndex}`,
                dayIndex,
                startSlot: Math.max(0, startSlot),
                endSlot,
                color: getBlockColor(block),
                title: block.unavailable_block_type_name || block.unavailable_block_type?.name || 'Unavailable'
              });
            } 
            else if (isSameDay(endDate, day)) {
              // This is the last day of the block
              const endSlot = timeToSlotIndex(endDate) - 1;
              
              result.push({
                id: `${block.id}-day-${dayIndex}`,
                dayIndex,
                startSlot: 0, // Start at the beginning of the day
                endSlot: Math.min(endSlot, (END_HOUR - START_HOUR) * 4 - 1),
                color: getBlockColor(block),
                title: block.unavailable_block_type_name || block.unavailable_block_type?.name || 'Unavailable'
              });
            }
            else if (startDate < day && endDate > day) {
              // This is a middle day (fully blocked)
              result.push({
                id: `${block.id}-day-${dayIndex}`,
                dayIndex,
                startSlot: 0,
                endSlot: (END_HOUR - START_HOUR) * 4 - 1,
                color: getBlockColor(block), 
                title: block.unavailable_block_type_name || block.unavailable_block_type?.name || 'Unavailable'
              });
            }
          });
        } else {
          // Regular single-day block
          const dayIndex = weekDays.findIndex(day => isSameDay(startDate, day));
          if (dayIndex === -1) return []; // Skip if not in this week
          
          // Calculate start and end slots
          const startSlot = timeToSlotIndex(startDate);
          const endSlot = timeToSlotIndex(endDate) - 1; // Subtract 1 because the end slot is exclusive
          
          result.push({
            id: block.id,
            dayIndex,
            startSlot: Math.max(0, startSlot), // Ensure we don't go before the calendar start
            endSlot: Math.min(endSlot, (END_HOUR - START_HOUR) * 4 - 1), // Ensure we don't go past calendar end
            color: getBlockColor(block), // Get color based on block type
            title: block.unavailable_block_type_name || block.unavailable_block_type?.name || 'Unavailable'
          });
        }
        
        return result;
      })
      .filter(Boolean) as Array<{
        id: string;
        dayIndex: number;
        startSlot: number;
        endSlot: number;
        color: string;
        title: string;
      }>;
  };

  // Check if slot is in an existing unavailable block
  const isSlotUnavailable = (dayIndex: number, slotIndex: number): boolean => {
    // If we have no days or no blocks, nothing is unavailable
    if (!weekDays.length) return false;
    
    // First check blocks from the database
    if (unavailableBlocks.length > 0) {
      const existingBlocks = processUnavailableBlocks();
      
      // Check if this slot is in any processed unavailable block
      const isInExistingBlock = existingBlocks.some(block => 
        block.dayIndex === dayIndex && 
        slotIndex >= block.startSlot && 
        slotIndex <= block.endSlot
      );
      
      if (isInExistingBlock) return true;
    }
    
    // Then check blocks created in this session
    return createdBlocks.some(block => 
      block.dayIndex === dayIndex && 
      slotIndex >= block.startSlot && 
      slotIndex <= block.endSlot
    );
  };
  
  // Check if a range overlaps with existing unavailable blocks
  const doesRangeOverlap = (dayIndex: number, startSlot: number, endSlot: number): {overlaps: boolean, blockInfo?: string} => {
    // Check blocks from database first
    if (unavailableBlocks.length > 0) {
      const existingBlocks = processUnavailableBlocks();
      
      for (const block of existingBlocks) {
        if (block.dayIndex === dayIndex) {
          // Check if the ranges overlap
          if (
            (startSlot <= block.endSlot && endSlot >= block.startSlot) ||
            (block.startSlot <= endSlot && block.endSlot >= startSlot)
          ) {
            return { 
              overlaps: true, 
              blockInfo: `This time overlaps with an existing "${block.title}" block` 
            };
          }
        }
      }
    }
    
    // Then check blocks created in this session
    for (const block of createdBlocks) {
      if (block.dayIndex === dayIndex) {
        // Check if the ranges overlap
        if (
          (startSlot <= block.endSlot && endSlot >= block.startSlot) ||
          (block.startSlot <= endSlot && block.endSlot >= startSlot)
        ) {
          return { 
            overlaps: true, 
            blockInfo: `This time overlaps with a block you just created` 
          };
        }
      }
    }
    
    return { overlaps: false };
  };

  // Handle time slot click
  const handleTimeSlotClick = (dayIndex: number, slotIndex: number) => {
    // Check if this slot is already in an unavailable block
    if (isSlotUnavailable(dayIndex, slotIndex)) {
      setMessage('This time is already marked as unavailable');
      setShowMessage(true);
      return;
    }
    
    if (selectionPhase === 'none' || selectionPhase === 'complete') {
      // First click (start time)
      const { day, hour, minute } = slotToTime(dayIndex, slotIndex);
      
      setFirstSelection({ day: dayIndex, slot: slotIndex });
      setSecondSelection(null);
      
      // Set initial selected range (just the first point)
      setSelectedRange({
        startDay: day,
        startHour: hour,
        startMinute: minute,
        endDay: day,
        endHour: hour,
        endMinute: minute + 15
      });
      
      // Update selection phase
      setSelectionPhase('first');
      setMessage('Now click on the ending time slot');
      setShowMessage(true);
      
    } else if (selectionPhase === 'first' && firstSelection) {
      // Second click (end time)
      // If clicking on a different day, don't allow it
      if (dayIndex !== firstSelection.day) {
        setMessage('Please select a time on the same day');
        setShowMessage(true);
        return;
      }
      
      // Get start and end slots in chronological order
      const startSlot = Math.min(firstSelection.slot, slotIndex);
      const endSlot = Math.max(firstSelection.slot, slotIndex);
      
      // Check if any part of the selection overlaps with existing blocks
      const { overlaps, blockInfo } = doesRangeOverlap(dayIndex, startSlot, endSlot);
      if (overlaps) {
        setMessage(blockInfo || 'This time overlaps with an existing unavailable block');
        setShowMessage(true);
        return;
      }
      
      const { day: startDay, hour: startHour, minute: startMinute } = 
        slotToTime(dayIndex, startSlot);
      
      const { day: endDay, hour: endHour, minute: endMinute } = 
        slotToTime(dayIndex, endSlot);
      
      // End time is 15 minutes after the last slot
      let adjustedEndHour = endHour;
      let adjustedEndMinute = endMinute + 15;
      
      // Adjust for minute overflow
      if (adjustedEndMinute >= 60) {
        adjustedEndHour += 1;
        adjustedEndMinute -= 60;
      }
      
      // Set the full selected range
      setSelectedRange({
        startDay: startDay,
        startHour: startHour,
        startMinute: startMinute,
        endDay: endDay,
        endHour: adjustedEndHour,
        endMinute: adjustedEndMinute
      });
      
      // Save the second selection
      setSecondSelection({ day: dayIndex, slot: slotIndex });
      
      // Mark selection as complete
      setSelectionPhase('complete');
      
      // Open dialog to confirm
      setConfirmDialogOpen(true);
    }
  };

  // Calculate selection rectangle coordinates
  const getSelectionStyles = () => {
    if (selectionPhase === 'none' || !firstSelection) return null;
    
    const dayIndex = firstSelection.day;
    
    if (selectionPhase === 'first') {
      // Just highlight the first selected slot
      const top = firstSelection.slot * TIME_SLOT_HEIGHT;
      const height = TIME_SLOT_HEIGHT;
      
      return {
        backgroundColor: 'rgba(25, 118, 210, 0.2)',
        border: '2px solid rgba(25, 118, 210, 0.6)',
        borderRadius: '4px',
        zIndex: 10,
        position: 'absolute',
        top: `${top}px`,
        left: `calc(70px + (${dayIndex} * ((100% - 70px) / 7)))`,
        width: 'calc((100% - 70px) / 7)',
        height: `${height}px`,
      };
    } else if (selectionPhase === 'complete' && secondSelection) {
      // Highlight the range between first and second selection
      const startSlot = Math.min(firstSelection.slot, secondSelection.slot);
      const endSlot = Math.max(firstSelection.slot, secondSelection.slot);
      
      const top = startSlot * TIME_SLOT_HEIGHT;
      const height = (endSlot - startSlot + 1) * TIME_SLOT_HEIGHT;
      
      return {
        backgroundColor: 'rgba(244, 67, 54, 0.2)',
        border: '2px solid rgba(244, 67, 54, 0.6)',
        borderRadius: '4px',
        zIndex: 10,
        position: 'absolute',
        top: `${top}px`,
        left: `calc(70px + (${dayIndex} * ((100% - 70px) / 7)))`,
        width: 'calc((100% - 70px) / 7)',
        height: `${height}px`,
      };
    }
    
    return null;
  };

  // Handle creation of unavailable block
  const handleCreateBlock = async () => {
    if (
      !selectedRange.startDay ||
      selectedRange.startHour === null ||
      selectedRange.startMinute === null ||
      !selectedRange.endDay ||
      selectedRange.endHour === null ||
      selectedRange.endMinute === null ||
      !firstSelection ||
      !secondSelection
    ) {
      setError('Invalid time selection');
      return;
    }
    
    setIsCreating(true);
    setError(null);
    
    try {
      // Create start and end dates
      const startDate = new Date(selectedRange.startDay);
      startDate.setHours(selectedRange.startHour, selectedRange.startMinute, 0, 0);
      
      const endDate = new Date(selectedRange.endDay);
      endDate.setHours(selectedRange.endHour, selectedRange.endMinute, 0, 0);
      
      // Make sure we're using the correct practitioner ID
      if (!practitionerId) {
        setError('No practitioner selected');
        return;
      }
      
      console.log(`Creating unavailable block for practitioner ID: ${practitionerId}`);
      
      // Create unavailable block with the specific practitioner ID
      console.log(`Creating unavailable block with practitioner ID: ${practitionerId}, from ${startDate.toISOString()} to ${endDate.toISOString()}`);
      const result = await createUnavailableBlock({
        practitionerId: practitionerId,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        notes: blockNotes,
        unavailableBlockTypeId: blockTypeId || undefined
      });
      
      console.log('Unavailable block creation result:', result);
      
      // Show success message
      setMessage('Unavailable block created successfully!');
      setShowMessage(true);
      
      // Save this block's position for visual feedback
      const dayIndex = firstSelection.day;
      const startSlot = Math.min(firstSelection.slot, secondSelection.slot);
      const endSlot = Math.max(firstSelection.slot, secondSelection.slot);
      
      setCreatedBlocks(prev => [
        ...prev,
        {
          dayIndex,
          startSlot,
          endSlot
        }
      ]);
      
      // Refresh unavailable blocks from the server
      fetchUnavailableBlocks();
      
      // Close dialog and notify parent
      setConfirmDialogOpen(false);
      onBlocksCreated();
      
      // Reset selection for next block
      setSelectionPhase('none');
      setFirstSelection(null);
      setSecondSelection(null);
      setSelectedRange({
        startDay: null,
        startHour: null,
        startMinute: null,
        endDay: null,
        endHour: null,
        endMinute: null
      });
      
      // Keep notes and block type for convenience 
      // (in case user wants to create similar blocks)
    } catch (err: any) {
      console.error('Error creating unavailable block:', err);
      setError(err.response?.data?.error || 'Failed to create unavailable block');
    } finally {
      setIsCreating(false);
    }
  };

  // Format time for display
  const formatTime = (hour: number, minute: number): string => {
    const hourDisplay = hour % 12 === 0 ? 12 : hour % 12;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    return `${hourDisplay}:${minute.toString().padStart(2, '0')} ${ampm}`;
  };
  
  // Cancel the current selection
  const handleCancelSelection = () => {
    setSelectionPhase('none');
    setFirstSelection(null);
    setSecondSelection(null);
    setSelectedRange({
      startDay: null,
      startHour: null,
      startMinute: null,
      endDay: null,
      endHour: null,
      endMinute: null
    });
  };

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          margin: '0 auto',
          overflow: 'hidden',
          bgcolor: 'transparent',
          maxWidth: '100%'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box display="flex" alignItems="center">
            <BlockIcon sx={{ color: 'error.main', mr: 1 }} />
            <Typography variant="h5" fontWeight="bold">
              Unavailable Time
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              startIcon={<TodayIcon />}
              onClick={handleCurrentWeek}
              variant="outlined"
              size="small"
            >
              Current Week
            </Button>
            <IconButton onClick={handlePrevWeek} size="small">
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={handleNextWeek} size="small">
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
            <IconButton 
              onClick={() => {
                fetchUnavailableBlocks();
                setMessage('Refreshed unavailable blocks');
                setShowMessage(true);
              }} 
              size="small"
              color="primary"
              disabled={isLoading}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
            <Button onClick={onClose} color="inherit">
              Close
            </Button>
          </Box>
        </Box>
        
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500 }}>
          {format(weekStart, 'MMMM d', { locale: enAU })} - {format(addDays(weekStart, 6), 'MMMM d, yyyy', { locale: enAU })}
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          height: '600px',
          maxWidth: '100%'
        }}>
          {/* Week header */}
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: '70px repeat(7, 1fr)',
            borderBottom: '1px solid',
            borderColor: 'divider',
            backgroundColor: 'primary.main',
            color: 'primary.contrastText'
          }}>
            <Box sx={{ 
              p: 1, 
              borderRight: '1px solid',
              borderColor: 'divider',
              bgcolor: 'primary.dark',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <Typography variant="subtitle2" fontWeight="bold">
                Time
              </Typography>
            </Box>
            
            {weekDays.map((day, i) => (
              <Box key={i} sx={{ 
                p: 1, 
                textAlign: 'center',
                borderRight: i < 6 ? '1px solid' : 'none',
                borderColor: 'divider',
                bgcolor: isToday(day) ? 'secondary.main' : 'primary.main'
              }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {format(day, 'EEE', { locale: enAU })}
                </Typography>
                <Typography variant="body2">
                  {format(day, 'd', { locale: enAU })}
                </Typography>
              </Box>
            ))}
          </Box>
          
          {/* Time grid */}
          <Box 
            ref={timeGridRef}
            sx={{ 
              display: 'grid',
              gridTemplateColumns: '70px repeat(7, 1fr)',
              gridTemplateRows: `repeat(${(END_HOUR - START_HOUR) * 4}, ${TIME_SLOT_HEIGHT}px)`,
              position: 'relative',
              flexGrow: 1,
              overflowY: 'auto'
            }}
          >
            {/* Time labels */}
            {Array.from({ length: END_HOUR - START_HOUR }).map((_, i) => (
              <React.Fragment key={`hour-${i}`}>
                <Box sx={{ 
                  gridColumn: '1',
                  gridRow: `span 4`,
                  borderRight: '1px solid',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.100'
                }}>
                  <Typography variant="caption" sx={{ 
                    transform: 'translateY(-50%)',
                    color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'inherit'
                  }}>
                    {formatTime(i + START_HOUR, 0)}
                  </Typography>
                </Box>
              </React.Fragment>
            ))}
            
            {/* Time slots */}
            {weekDays.map((day, dayIndex) => (
              Array.from({ length: (END_HOUR - START_HOUR) * 4 }).map((_, slotIndex) => {
                const isHourStart = slotIndex % 4 === 0;
                const isHalfHour = slotIndex % 4 === 2;
                
                // Is this slot the first selection?
                const isFirstSelection = 
                  firstSelection !== null && 
                  firstSelection.day === dayIndex && 
                  firstSelection.slot === slotIndex;
                
                // Is this slot within the selection range?
                const isInSelectionRange = 
                  selectionPhase === 'complete' &&
                  firstSelection !== null &&
                  secondSelection !== null &&
                  dayIndex === firstSelection.day &&
                  slotIndex >= Math.min(firstSelection.slot, secondSelection.slot) &&
                  slotIndex <= Math.max(firstSelection.slot, secondSelection.slot);
                
                return (
                  <Box
                    key={`day-${dayIndex}-slot-${slotIndex}`}
                    sx={{
                      gridColumn: dayIndex + 2,
                      gridRow: slotIndex + 1,
                      borderRight: dayIndex < 6 ? '1px solid' : 'none',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: isFirstSelection
                        ? 'primary.light'
                        : isInSelectionRange
                          ? 'error.light'
                          : isSlotUnavailable(dayIndex, slotIndex)
                            ? theme.palette.mode === 'dark' 
                              ? 'rgba(244, 67, 54, 0.25)' // Darker red for dark mode
                              : 'rgba(244, 67, 54, 0.15)' // Light red for light mode
                            : isHourStart 
                              ? theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.03)'
                                : 'rgba(0, 0, 0, 0.03)' 
                              : isHalfHour 
                                ? theme.palette.mode === 'dark'
                                  ? 'rgba(255, 255, 255, 0.01)'
                                  : 'rgba(0, 0, 0, 0.01)'
                                : theme.palette.mode === 'dark'
                                  ? 'rgba(0, 0, 0, 0.2)'
                                  : 'white',
                      cursor: isSlotUnavailable(dayIndex, slotIndex) ? 'not-allowed' : 'pointer',
                      height: TIME_SLOT_HEIGHT,
                      '&:hover': {
                        bgcolor: isSlotUnavailable(dayIndex, slotIndex) 
                          ? theme.palette.mode === 'dark'
                            ? 'rgba(244, 67, 54, 0.35)' // Darker red on hover in dark mode
                            : 'rgba(244, 67, 54, 0.25)' // Slightly darker on hover in light mode
                          : theme.palette.mode === 'dark'
                            ? 'rgba(25, 118, 210, 0.15)' // Darker blue on hover in dark mode
                            : 'rgba(25, 118, 210, 0.08)' // Light blue on hover in light mode
                      },
                      position: 'relative'
                    }}
                    onClick={() => handleTimeSlotClick(dayIndex, slotIndex)}
                  >
                    {isFirstSelection && (
                      <Box sx={{ 
                        height: '100%', 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        color: 'primary.main'
                      }}>
                        <Typography variant="caption">Start</Typography>
                      </Box>
                    )}
                    {isSlotUnavailable(dayIndex, slotIndex) && (
                      <Box sx={{
                        position: 'absolute',
                        right: 0,
                        top: 0,
                        left: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.4)' : 'rgba(244, 67, 54, 0.3)', // More visible background
                        borderLeft: theme.palette.mode === 'dark' ? '4px solid rgba(255, 99, 71, 0.9)' : '4px solid rgba(244, 67, 54, 0.9)',
                        boxShadow: 'inset 0 0 4px rgba(244, 67, 54, 0.5)', // Add inner shadow for emphasis
                        zIndex: 10, // Ensure it's above other elements
                      }}>
                        {slotIndex % 4 === 0 && (
                          <Typography variant="caption" sx={{ 
                            color: 'white', 
                            fontWeight: 'bold',
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 99, 71, 0.9)' : 'error.main',
                            px: 1,
                            py: 0.2,
                            borderRadius: 1,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }}>
                            Unavailable
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })
            ))}
            
            {/* Selection overlay */}
            {getSelectionStyles() && (
              <Box
                sx={{
                  ...getSelectionStyles(),
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontWeight: 'bold',
                  color: selectionPhase === 'first' ? 'primary.main' : 'error.main'
                }}
              >
                {selectionPhase === 'first' && firstSelection && (
                  <Typography variant="caption" sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    p: 0.5,
                    borderRadius: 1
                  }}>
                    Click on end time
                  </Typography>
                )}
                
                {selectionPhase === 'complete' && 
                 selectedRange.startHour !== null && selectedRange.startMinute !== null && 
                 selectedRange.endHour !== null && selectedRange.endMinute !== null && (
                  <Typography variant="caption" sx={{ 
                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                    p: 0.5,
                    borderRadius: 1
                  }}>
                    {formatTime(selectedRange.startHour, selectedRange.startMinute)} - 
                    {formatTime(selectedRange.endHour, selectedRange.endMinute)}
                  </Typography>
                )}
              </Box>
            )}
            
            {/* Newly created blocks */}
            {createdBlocks.map((block, index) => {
              // Calculate dimensions and position
              const top = block.startSlot * TIME_SLOT_HEIGHT;
              const height = (block.endSlot - block.startSlot + 1) * TIME_SLOT_HEIGHT;
              const left = `calc(70px + (${block.dayIndex} * ((100% - 70px) / 7)))`;
              const width = 'calc((100% - 70px) / 7)';
              
              // Format time for display
              const startSlotTime = slotToTime(block.dayIndex, block.startSlot);
              const endSlotTime = slotToTime(block.dayIndex, block.endSlot);
              const timeDisplay = `${formatTime(startSlotTime.hour, startSlotTime.minute)} - ${formatTime(endSlotTime.hour, endSlotTime.minute)}`;
              
              return (
                <Box
                  key={`created-block-${index}`}
                  sx={{
                    position: 'absolute',
                    top: `${top}px`,
                    left,
                    width,
                    height: `${height}px`,
                    backgroundColor: 'rgba(76, 175, 80, 0.3)',
                    border: '3px solid rgba(76, 175, 80, 0.8)',
                    borderRadius: '4px',
                    zIndex: 55, // Higher than existing blocks
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                    padding: 1,
                    cursor: 'default'
                  }}
                >
                  <Tooltip title={`Just created: ${timeDisplay}`}>
                    <Box sx={{ textAlign: 'center', width: '100%' }}>
                      <Typography variant="caption" sx={{ 
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'white',
                        color: 'success.main',
                        p: 0.5,
                        borderRadius: 1,
                        fontWeight: 'bold',
                        display: 'inline-block',
                        mb: 0.5,
                        whiteSpace: 'nowrap'
                      }}>
                        Just Added
                      </Typography>
                      
                      {height > 60 && (
                        <Typography variant="caption" sx={{ 
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255,255,255,0.9)',
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'text.secondary',
                          p: 0.5,
                          borderRadius: 1,
                          display: 'block',
                          fontSize: '0.7rem',
                          whiteSpace: 'nowrap'
                        }}>
                          {timeDisplay}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              );
            })}
            
            {/* Existing unavailable blocks from database */}
            {processUnavailableBlocks().map((block) => {
              // Calculate dimensions and position
              const top = block.startSlot * TIME_SLOT_HEIGHT;
              const height = (block.endSlot - block.startSlot + 1) * TIME_SLOT_HEIGHT;
              const left = `calc(70px + (${block.dayIndex} * ((100% - 70px) / 7)))`;
              const width = 'calc((100% - 70px) / 7)';
              
              // Format time for display
              const startSlotTime = slotToTime(block.dayIndex, block.startSlot);
              const endSlotTime = slotToTime(block.dayIndex, block.endSlot);
              const timeDisplay = `${formatTime(startSlotTime.hour, startSlotTime.minute)} - ${formatTime(endSlotTime.hour, endSlotTime.minute)}`;
              
              // Ensure block.color is a string
              const blockColor = block.color || 'rgba(244, 67, 54, 0.7)';
              // Ensure block.title is a string
              const blockTitle = block.title || 'Unavailable';
              // Ensure block.id is a string
              const blockId = block.id || `unavailable-${block.dayIndex}-${block.startSlot}-${block.endSlot}`;
              
              return (
                <Box
                  key={`unavailable-block-${blockId}`}
                  sx={{
                    position: 'absolute',
                    top: `${top}px`,
                    left,
                    width,
                    height: `${height}px`,
                    backgroundColor: 'rgba(244, 67, 54, 0.3)',
                    border: `3px solid ${blockColor}`,
                    borderRadius: '4px',
                    zIndex: 50, // Higher zIndex to ensure visibility
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                    padding: 1,
                    overflow: 'hidden',
                    cursor: 'default'
                  }}
                >
                  <Tooltip title={`Unavailable: ${blockTitle} (${timeDisplay})`}>
                    <Box sx={{ textAlign: 'center', width: '100%' }}>
                      <Typography variant="caption" sx={{ 
                        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'white',
                        color: 'error.main',
                        p: 0.5,
                        borderRadius: 1,
                        fontWeight: 'bold',
                        display: 'inline-block',
                        mb: 0.5,
                        whiteSpace: 'nowrap'
                      }}>
                        {blockTitle}
                      </Typography>
                      
                      {height > 60 && (
                        <Typography variant="caption" sx={{ 
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255,255,255,0.9)',
                          color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'text.secondary',
                          p: 0.5,
                          borderRadius: 1,
                          display: 'block',
                          fontSize: '0.7rem',
                          whiteSpace: 'nowrap'
                        }}>
                          {timeDisplay}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              );
            })}
          </Box>
        </Box>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="body2" color="info.main" fontWeight="medium">
              ✓ Click a start time, then click an end time to select a block
            </Typography>
            {createdBlocks.length > 0 && (
              <Typography variant="body2" color="success.main" fontWeight="medium">
                ✓ {createdBlocks.length} time block{createdBlocks.length !== 1 ? 's' : ''} created this session
              </Typography>
            )}
            {isLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                <Typography variant="body2" color="text.secondary" fontWeight="medium">
                  Loading existing unavailable blocks...
                </Typography>
              </Box>
            ) : (
              unavailableBlocks.length > 0 && (
                <Typography variant="body2" color="text.secondary" fontWeight="medium" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ color: 'error.main', mr: 1 }}>●</Box>
                  {unavailableBlocks.length} existing unavailable block{unavailableBlocks.length !== 1 ? 's' : ''} shown
                </Typography>
              )
            )}
            {/* Display refresh buttons always */}
            <Box sx={{ mt: 1 }}>
              <Button 
                size="small"
                variant="contained"
                color="success"
                onClick={fetchUnavailableBlocksDirect}
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
                sx={{ minWidth: '160px' }}
              >
                Show Existing Blocks
              </Button>
            </Box>
          </Box>
          
          <Box>
            {selectionPhase === 'first' && (
              <Button 
                variant="outlined" 
                color="error" 
                onClick={handleCancelSelection}
                sx={{ mr: 1 }}
              >
                Cancel Selection
              </Button>
            )}
            
            <Button 
              variant="contained" 
              color="error" 
              startIcon={<BlockIcon />}
              onClick={() => {
                if (selectionPhase === 'complete') {
                  setConfirmDialogOpen(true);
                } else {
                  setMessage('Please select both a start and end time first');
                  setShowMessage(true);
                }
              }}
              size="large"
              sx={{ fontWeight: 'bold', px: 3, minWidth: '160px' }}
              disabled={selectionPhase !== 'complete'}
            >
              Create Block
            </Button>
          </Box>
        </Box>
      </Paper>
      
      {/* Snackbar for messages */}
      <Snackbar
        open={showMessage}
        autoHideDuration={5000}
        onClose={() => setShowMessage(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setShowMessage(false)} severity="info" sx={{ width: '100%' }}>
          {message}
        </Alert>
      </Snackbar>
      
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center">
              <BlockIcon sx={{ color: 'error.main', mr: 1 }} />
              <Typography variant="h6">
                Confirm Unavailable Block
              </Typography>
            </Box>
            <IconButton onClick={() => setConfirmDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent>
          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Selected Time
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'rgba(244, 67, 54, 0.1)' }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {selectedRange.startDay && format(selectedRange.startDay, 'EEEE, MMMM d, yyyy', { locale: enAU })}
                </Typography>
                
                <Stack direction="row" spacing={2} alignItems="center">
                  {/* Start Time Selection */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Start Time
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="start-hour-label">Hour</InputLabel>
                        <Select
                          labelId="start-hour-label"
                          value={selectedRange.startHour || ''}
                          label="Hour"
                          onChange={(e) => {
                            const hour = e.target.value as number;
                            setSelectedRange({
                              ...selectedRange,
                              startHour: hour
                            });
                          }}
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <MenuItem key={`start-hour-${i}`} value={i}>
                              {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      <FormControl fullWidth size="small">
                        <InputLabel id="start-minute-label">Minute</InputLabel>
                        <Select
                          labelId="start-minute-label"
                          value={selectedRange.startMinute || ''}
                          label="Minute"
                          onChange={(e) => {
                            const minute = e.target.value as number;
                            setSelectedRange({
                              ...selectedRange,
                              startMinute: minute
                            });
                          }}
                        >
                          <MenuItem value={0}>00</MenuItem>
                          <MenuItem value={15}>15</MenuItem>
                          <MenuItem value={30}>30</MenuItem>
                          <MenuItem value={45}>45</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                  </Box>
                  
                  <Typography variant="body1" sx={{ pt: 3 }}>to</Typography>
                  
                  {/* End Time Selection */}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      End Time
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="end-hour-label">Hour</InputLabel>
                        <Select
                          labelId="end-hour-label"
                          value={selectedRange.endHour || ''}
                          label="Hour"
                          onChange={(e) => {
                            const hour = e.target.value as number;
                            setSelectedRange({
                              ...selectedRange,
                              endHour: hour
                            });
                          }}
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <MenuItem key={`end-hour-${i}`} value={i}>
                              {i === 0 ? '12 AM' : i < 12 ? `${i} AM` : i === 12 ? '12 PM' : `${i - 12} PM`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      
                      <FormControl fullWidth size="small">
                        <InputLabel id="end-minute-label">Minute</InputLabel>
                        <Select
                          labelId="end-minute-label"
                          value={selectedRange.endMinute || ''}
                          label="Minute"
                          onChange={(e) => {
                            const minute = e.target.value as number;
                            setSelectedRange({
                              ...selectedRange,
                              endMinute: minute
                            });
                          }}
                        >
                          <MenuItem value={0}>00</MenuItem>
                          <MenuItem value={15}>15</MenuItem>
                          <MenuItem value={30}>30</MenuItem>
                          <MenuItem value={45}>45</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            </Box>
            
            {/* Block Type */}
            <FormControl component="fieldset">
              <Typography variant="subtitle2" gutterBottom>
                Block Type
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 1,
                mt: 1
              }}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    border: '1px solid',
                    borderColor: blockTypeId === 'lunch' ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    backgroundColor: blockTypeId === 'lunch' ? 'primary.light' : 'background.paper',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                  onClick={() => setBlockTypeId('lunch')}
                >
                  <Typography variant="body1" fontWeight={blockTypeId === 'lunch' ? 'bold' : 'normal'}>
                    Lunch Break
                  </Typography>
                </Box>
                
                <Box 
                  sx={{ 
                    p: 1.5, 
                    border: '1px solid',
                    borderColor: blockTypeId === 'meeting' ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    backgroundColor: blockTypeId === 'meeting' ? 'primary.light' : 'background.paper',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                  onClick={() => setBlockTypeId('meeting')}
                >
                  <Typography variant="body1" fontWeight={blockTypeId === 'meeting' ? 'bold' : 'normal'}>
                    Meeting / Appointment
                  </Typography>
                </Box>
                
                <Box 
                  sx={{ 
                    p: 1.5, 
                    border: '1px solid',
                    borderColor: blockTypeId === 'out' ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    backgroundColor: blockTypeId === 'out' ? 'primary.light' : 'background.paper',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                  onClick={() => setBlockTypeId('out')}
                >
                  <Typography variant="body1" fontWeight={blockTypeId === 'out' ? 'bold' : 'normal'}>
                    Out of Office
                  </Typography>
                </Box>
                
                <Box 
                  sx={{ 
                    p: 1.5, 
                    border: '1px solid',
                    borderColor: blockTypeId === '' ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    backgroundColor: blockTypeId === '' ? 'primary.light' : 'background.paper',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                  onClick={() => setBlockTypeId('')}
                >
                  <Typography variant="body1" fontWeight={blockTypeId === '' ? 'bold' : 'normal'}>
                    Custom Time Block
                  </Typography>
                </Box>
              </Box>
            </FormControl>
            
            {/* Notes */}
            <TextField
              label="Notes"
              multiline
              rows={3}
              fullWidth
              value={blockNotes}
              onChange={(e) => setBlockNotes(e.target.value)}
              placeholder="Why is this time unavailable? (optional)"
            />
          </Stack>
          
          {error && (
            <Box sx={{ mt: 2, color: 'error.main' }}>
              <Typography variant="body2">{error}</Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateBlock}
            variant="contained"
            color="error"
            startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : <BlockIcon />}
            disabled={isCreating}
            sx={{ minWidth: '160px' }}
          >
            {isCreating ? 'Creating...' : 'Create Block'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WeekViewCalendar;